import { useState, useEffect, useMemo } from 'react';
import { IEvent } from '@trii/types/dist/Calendar';

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  startAt?: Date | string;
  endAt?: Date | string;
  allDay?: boolean;
  color?: string;
  calendarId?: string;
  details?: string;
  location?: string;
  isPrivate?: boolean;
  rule?: string;
  relatedContact?: {
    id: string;
    name: string;
  };
  sharedWith?: Array<{ id: string }>;
  finalized?: boolean;
  kind?: 'event' | 'task';
  originalOccurrenceDate?: string;
  parentId?: string;
}

export interface UseCalendarEventsType {
  events: CalendarEvent[];
  setEvents: React.Dispatch<React.SetStateAction<CalendarEvent[]>>;
  filteredEvents: CalendarEvent[];
}

export const useCalendarEventsInitialState: UseCalendarEventsType = {
  events: [],
  setEvents: () => {},
  filteredEvents: [],
};

export const useCalendarEvents = (
  activeCalendarIds: string[],
  activities: IEvent[],
): UseCalendarEventsType => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);

  // Convertir actividades a formato de eventos del calendario
  useEffect(() => {
    if (!activities?.length) {
      setEvents([]);
      return;
    }

    const mappedEvents: CalendarEvent[] = activities.map((activity: any) => ({
      id: activity.id,
      title: activity.title || '',
      start: new Date(activity.startAt),
      end: new Date(activity.endAt || activity.startAt),
      startAt: activity.startAt,
      endAt: activity.endAt,
      allDay: activity.allDay || false,
      color: activity.color,
      calendarId: activity.calendarId,
      details: activity.details,
      location: activity.location,
      isPrivate: activity.isPrivate,
      rule: activity.rule,
      relatedContact: activity.relatedContact,
      sharedWith: activity.sharedWith,
      finalized: activity.finalized,
      kind: 'event',
      originalOccurrenceDate: activity.originalOccurrenceDate,
      parentId: activity.parentId,
    }));

    setEvents(mappedEvents);
  }, [activities]);

  // Filtrar eventos por calendarios activos
  const filteredEvents = useMemo(() => {
    if (!activeCalendarIds?.length) return events;
    return events.filter(
      (event) => event.calendarId && activeCalendarIds.includes(event.calendarId),
    );
  }, [events, activeCalendarIds]);

  // 🔒 ESTABILIZACIÓN: Memoizar el objeto para evitar recreaciones innecesarias
  return useMemo(
    () => ({
      events,
      setEvents,
      filteredEvents,
    }),
    [events, filteredEvents],
  );
};

export default useCalendarEvents;
